import {
  Box,
  Button,
  Group,
  Space,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconSparkles, IconVideo } from '@tabler/icons-react';
import { LoginWrapper } from '../LoginWrapper';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageRoutes } from '../../constants/routes';
import { primaryColor } from '../../constants/theme';
import { useOnboarding } from './SignUpContext';

export enum ButtonOptions {
  HOST = 'host',
  CONNECT = 'connect',
}

const OptionButton = ({
  option,
  selectedOption,
  setSelectedOption,
}: {
  option: ButtonOptions;
  selectedOption?: ButtonOptions;
  setSelectedOption: (opt: ButtonOptions) => void;
}) => {
  const optionDetails = OPTION_DETAILS[option];
  const selected = selectedOption === option;

  return (
    <UnstyledButton
      onClick={() => setSelectedOption(option)}
      aria-pressed={selected}
      style={{ width: '100%' }}
    >
      <Box
        style={{
          borderRadius: 24,
          border: `2px solid ${selected ? primaryColor : 'var(--mantine-color-gray-4)'}`,
          backgroundColor: selected ? `${primaryColor}12` : 'var(--mantine-color-white)',
          padding: '16px 18px',
          textAlign: 'left',
          boxShadow: selected ? '0 10px 30px rgba(0, 0, 0, 0.04)' : 'none',
          transition: 'all 160ms ease',
        }}
      >
        <Group align="center" gap="md" wrap="nowrap">
          <Box
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              backgroundColor: 'var(--mantine-color-gray-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {optionDetails.icon}
          </Box>

          <Space w="md"></Space>

          <Stack
            gap={1}
            justify="center"
            align="flex-start"
            style={{ flex: 1, textAlign: 'left' }}
          >
            <Text fw={700} size="xl" c="dark.9">
              {optionDetails.title}
            </Text>
            <Text size="md" c="dimmed">
              {optionDetails.subtitle}
            </Text>
          </Stack>
        </Group>
      </Box>
    </UnstyledButton>
  );
};

const OPTION_DETAILS: Record<
  ButtonOptions,
  { title: string; subtitle: string; icon: React.ReactNode }
> = {
  [ButtonOptions.CONNECT]: {
    title: 'Book a 1:1 call',
    subtitle: 'Connect with creators I follow',
    icon: <IconVideo size={30} color={primaryColor} stroke={1.8} />,
  },
  [ButtonOptions.HOST]: {
    title: 'Host 1:1 calls',
    subtitle: 'Let my audience book time with me',
    icon: <IconSparkles size={30} color={primaryColor} stroke={1.8} />,
  },
};

export const BestDescribes = () => {
  const [selectedOption, setSelectedOption] = useState<ButtonOptions>();
  const navigate = useNavigate();
  const { form } = useOnboarding();

  const handleSelectOption = (option: ButtonOptions) => {
    setSelectedOption(option);
    form.setFieldValue('isProfessional', option === ButtonOptions.HOST);
  };

  return (
    <LoginWrapper animate={false}>
      <Stack gap="xl" w="100%" maw={420} mx="auto" align="stretch">
        <Stack gap={6} align="center" px={{ base: 'xs', sm: 0 }}>
          <Title order={2} ta="center" fw="normal">
            Let&apos;s get you started
          </Title>
          <Text ta="center" size="lg" c="dimmed">
            What would you like to do?
          </Text>
        </Stack>

        <Stack gap="md">
          <OptionButton
            option={ButtonOptions.CONNECT}
            selectedOption={selectedOption}
            setSelectedOption={handleSelectOption}
          />
          <OptionButton
            option={ButtonOptions.HOST}
            selectedOption={selectedOption}
            setSelectedOption={handleSelectOption}
          />
        </Stack>

        <Text ta="center" c="dimmed" size="md" mt="xs">
          Takes less than 30 seconds
        </Text>

        <Button
          fullWidth
          radius="lg"
          size="md"
          onClick={() => navigate(PageRoutes.ONBOARDING_YOUR_DETAILS)}
          color={primaryColor}
          disabled={selectedOption === undefined}
        >
          Continue
        </Button>
      </Stack>
    </LoginWrapper>
  );
};
